import React, { useEffect } from 'react';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import {
	DynamicSurfaceView,
	useAIContext,
	useViewAIActions,
	type ViewConfig,
} from '@atlassian/ai-smart-button-api';

import {
	DYNAMIC_SURFACE_PANEL_ID,
	getDynamicSurfacePanelHeaderElements,
	i18n,
	// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
} from '../ai-dynamic-surface/useDynamicSurfacePanel';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import type { ObjectSidebar } from '../common/types/object-sidebar-types';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import type { AISmartButtonSidebarAPI } from '../common/types/shared-types';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { isObjectSidebar } from '../common/utils/ai-smart-button-shared';

type AIFeatureViewProps = {
	/**
	 * Use sidebarApi instead. objectSidebar will be removed in a future release.
	 */
	sidebarApi: AISmartButtonSidebarAPI | ObjectSidebar;
	view: ViewConfig | undefined;
};

const atlaskitCssStyles = cssMap({
	heightStyles: {
		height: '100%',
		backgroundColor: token('color.background.input'),
	},
});

export const AIFeatureView = ({
	view,
	sidebarApi,
}: AIFeatureViewProps): React.JSX.Element | null => {
	const usesLegacyObjectSidebar = isObjectSidebar(sidebarApi);

	const { changeView } = useViewAIActions();
	const context = useAIContext();
	const panelTitle = context?.reference ? i18n.referencingPanelTitle : undefined;
	const { changeHeaderElements } = usesLegacyObjectSidebar
		? sidebarApi.actions
		: { changeHeaderElements: undefined };

	useEffect(() => {
		if (!changeHeaderElements) {
			return;
		}

		changeHeaderElements(DYNAMIC_SURFACE_PANEL_ID, {
			HeaderIcon: () => (
				<IconButton
					testId="ai-feature-view-back-button"
					appearance="subtle"
					label=""
					icon={ArrowLeftIcon}
					onClick={() => changeView(DynamicSurfaceView.Default)}
				/>
			),
			HeaderRightAlignedElement: view?.headerRightAlignedElement
				? () => view.headerRightAlignedElement
				: undefined,
		});
	}, [changeHeaderElements, changeView, view?.headerRightAlignedElement]);

	useEffect(() => {
		return () => {
			if (!changeHeaderElements) {
				return;
			}

			changeHeaderElements(
				DYNAMIC_SURFACE_PANEL_ID,
				getDynamicSurfacePanelHeaderElements({ panelTitle }),
			);
		};
		// Reset Header Elements on unmount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (!view) {
		return null;
	}

	return (
		<Stack xcss={atlaskitCssStyles.heightStyles}>
			<Box paddingInline="space.200" xcss={atlaskitCssStyles.heightStyles}>
				{view?.render()}
			</Box>
		</Stack>
	);
};
